import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { 
  Users, 
  Calendar, 
  CreditCard, 
  UserCheck,
  TrendingUp,
  Clock,
  AlertCircle,
  Activity,
  Bell,
  Stethoscope,
  FileText,
  ClipboardList,
  Settings,
  DollarSign,
  UserPlus,
  CalendarPlus,
  PlusCircle,
  CalendarCheck
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { dashboardApi, DashboardStats, DashboardAlert, appointmentsApi } from '../../lib/api';
import { useNavigate } from 'react-router-dom';
import PatientTestReports from '../../components/Patients/PatientTestReports';

// Admin Dashboard Component
const AdminDashboard: React.FC<{ stats: DashboardStats; alerts: DashboardAlert[]; navigate: any }> = ({ stats, alerts, navigate }) => {
  const summaryCards = [
    {
      name: 'Total Patients',
      value: stats.summary.totalPatients.toLocaleString(),
      change: stats.patients.growth > 0 ? `+${stats.patients.growth}%` : `${stats.patients.growth}%`,
      changeType: stats.patients.growth >= 0 ? 'increase' as const : 'decrease' as const,
      icon: Users,
      color: 'bg-blue-500',
      subtitle: `${stats.patients.newThisMonth} new this month`,
      action: () => navigate('/patients')
    },
    {
      name: 'Active Doctors',
      value: stats.summary.totalDoctors.toString(),
      change: '+0%',
      changeType: 'neutral' as const,
      icon: UserCheck,
      color: 'bg-green-500',
      subtitle: `${stats.doctors.total} total doctors`,
      action: () => navigate('/doctors')
    },
    {
      name: 'Today\'s Appointments',
      value: stats.summary.todayAppointments.toString(),
      change: stats.insights.busyDay.includes('busy') ? '+High' : 'Normal',
      changeType: stats.summary.todayAppointments > 10 ? 'increase' as const : 'neutral' as const,
      icon: Calendar,
      color: 'bg-purple-500',
      subtitle: `${stats.summary.upcomingAppointments} upcoming`,
      action: () => navigate('/appointments')
    },
    {
      name: 'Utilization Rate',
      value: `${stats.insights.utilizationRate}%`,
      change: stats.insights.utilizationRate > 70 ? 'High' : 'Optimal',
      changeType: stats.insights.utilizationRate > 70 ? 'increase' as const : 'neutral' as const,
      icon: Activity,
      color: 'bg-yellow-500',
      subtitle: 'Doctor scheduling efficiency',
      action: () => navigate('/appointments')
    }
  ];

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="text-gray-600">
          Hospital management overview and system statistics.
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {summaryCards.map((card) => (
          <div key={card.name} className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow" onClick={card.action}>
            <div className="flex items-center">
              <div className={`flex-shrink-0 ${card.color} p-3 rounded-lg`}>
                <card.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">{card.name}</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">{card.value}</div>
                    <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                      card.changeType === 'increase' ? 'text-green-600' : 
                      card.changeType === 'decrease' ? 'text-red-600' : 'text-gray-500'
                    }`}>
                      {card.change}
                    </div>
                  </dd>
                  <dd className="text-xs text-gray-500">{card.subtitle}</dd>
                </dl>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Rest of admin dashboard content... */}
    </div>
  );
};

// Doctor Dashboard Component
const DoctorDashboard: React.FC<{ navigate: any; user: any }> = ({ navigate, user }) => {
  const [myAppointments, setMyAppointments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDoctorData();
  }, []);

  const fetchDoctorData = async () => {
    try {
      if (user?.doctor?.id) {
        const response = await appointmentsApi.getAppointments({
          doctorId: user.doctor.id,
          limit: 10
        });
        if (response.data.success) {
          setMyAppointments(response.data.data?.appointments || []);
        }
      }
    } catch (error) {
      console.error('Error fetching doctor data:', error);
    } finally {
      setLoading(false);
    }
  };

  const todayAppointments = myAppointments.filter(apt => 
    new Date(apt.appointmentDate).toDateString() === new Date().toDateString()
  );

  const upcomingAppointments = myAppointments.filter(apt => 
    new Date(apt.appointmentDate) > new Date()
  ).slice(0, 5);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">
        Welcome back, Dr. {user.firstName}!
      </h1>

      {/* Quick Actions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/appointments')}>
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-blue-500 p-3 rounded-lg">
              <Calendar className="h-6 w-6 text-white" />
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-gray-500">My Appointments</p>
              <p className="text-2xl font-semibold text-gray-900">View All</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/patients')}>
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-green-500 p-3 rounded-lg">
              <Users className="h-6 w-6 text-white" />
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-gray-500">My Patients</p>
              <p className="text-2xl font-semibold text-gray-900">Manage</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/doctors/schedule')}>
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-purple-500 p-3 rounded-lg">
              <Clock className="h-6 w-6 text-white" />
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-gray-500">My Schedule</p>
              <p className="text-2xl font-semibold text-gray-900">Manage</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/settings')}>
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-gray-500 p-3 rounded-lg">
              <Settings className="h-6 w-6 text-white" />
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-gray-500">My Settings</p>
              <p className="text-2xl font-semibold text-gray-900">Configure</p>
            </div>
          </div>
        </div>
      </div>

      {/* Today's Schedule */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Today's Schedule</h3>
            <button 
              onClick={() => navigate('/doctors/schedule')}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
            >
              <Clock className="h-4 w-4 mr-1" />
              Manage Schedule
            </button>
          </div>
          {/* Schedule content */}
          <div className="text-center py-8 text-gray-500">
            <CalendarCheck className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium mb-2">Your schedule for today</p>
            <p className="text-sm">
              Click "Manage Schedule" to set your availability times for the week.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Nurse/Staff Dashboard Component
const StaffDashboard: React.FC<{ navigate: any; user: any }> = ({ navigate, user }) => {
  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Staff Dashboard</h1>
        <p className="text-gray-600">
          Welcome back! Quick access to patient management and appointments.
        </p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/patients')}>
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-600 mr-4" />
            <div>
              <p className="text-lg font-medium text-gray-900">Patients</p>
              <p className="text-sm text-gray-500">View patient records</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/appointments')}>
          <div className="flex items-center">
            <Calendar className="h-8 w-8 text-green-600 mr-4" />
            <div>
              <p className="text-lg font-medium text-gray-900">Appointments</p>
              <p className="text-sm text-gray-500">Manage scheduling</p>
            </div>
          </div>
        </div>

        {user?.role === 'BILLING_STAFF' && (
          <div className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/billing')}>
            <div className="flex items-center">
              <CreditCard className="h-8 w-8 text-purple-600 mr-4" />
              <div>
                <p className="text-lg font-medium text-gray-900">Billing</p>
                <p className="text-sm text-gray-500">Process payments</p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/settings')}>
          <div className="flex items-center">
            <Settings className="h-8 w-8 text-gray-600 mr-4" />
            <div>
              <p className="text-lg font-medium text-gray-900">Settings</p>
              <p className="text-sm text-gray-500">Personal preferences</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DashboardPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [alerts, setAlerts] = useState<DashboardAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = user?.role?.toUpperCase() === 'ADMIN';
  const isDoctor = user?.role?.toUpperCase() === 'DOCTOR';

  useEffect(() => {
    // Only fetch admin dashboard data for admins
    if (isAdmin) {
      fetchDashboardData();
    } else {
      setLoading(false);
    }
  }, [isAdmin]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsResponse, alertsResponse] = await Promise.all([
        dashboardApi.getStats(),
        dashboardApi.getAlerts()
      ]);

      if (statsResponse.data.success && statsResponse.data.data) {
        setStats(statsResponse.data.data);
      }

      if (alertsResponse.data.success && alertsResponse.data.data) {
        setAlerts(alertsResponse.data.data.alerts);
      }

    } catch (error: any) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data');
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 lg:space-y-6">
        <div>
          <div className="h-8 bg-gray-200 rounded animate-pulse mb-2"></div>
          <div className="h-4 bg-gray-200 rounded animate-pulse w-2/3"></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow p-6">
              <div className="h-20 bg-gray-200 rounded animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Render role-specific dashboard
  if (isAdmin) {
    if (error || !stats) {
      return (
        <div className="space-y-4 lg:space-y-6">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-red-600">Error loading dashboard data. Please try again.</p>
          </div>
          <button
            onClick={fetchDashboardData}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      );
    }
    return <AdminDashboard stats={stats} alerts={alerts} navigate={navigate} />;
  } else if (isDoctor) {
    return <DoctorDashboard navigate={navigate} user={user} />;
  } else if (user?.role?.toUpperCase() === 'PATIENT') {
    return (
      <div>
        {/* Existing patient dashboard content if any */}
        <PatientTestReports />
      </div>
    );
  } else {
    return <StaffDashboard navigate={navigate} user={user} />;
  }
};

export default DashboardPage;